import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Alert, Image } from 'react-native';
import Constants from 'expo-constants';
import { useRouter, useLocalSearchParams } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals } from '@/constants/theme';
import { getOnboardingCompleted } from '@/lib/onboarding-storage';
import { PENDING_INVITE_KEY, parseInviteTokenFromInput } from '@/lib/invite-links';
import { supabase } from '@/lib/supabase';

const LOG_PREFIX = '[SignIn]';

export default function SignInScreen() {
  const params = useLocalSearchParams<{ inviteToken?: string }>();
  const inviteTokenFromUrl = typeof params.inviteToken === 'string' ? params.inviteToken : undefined;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();

  const handleSignIn = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setError('');
    setLoading(true);

    try {
      if (!supabase) {
        Alert.alert('Error', 'Supabase is not configured. Please set environment variables.');
        setLoading(false);
        return;
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError('The email or password you entered is incorrect. Please try again.');
      } else if (data?.user) {
        const fromStorage = await AsyncStorage.getItem(PENDING_INVITE_KEY);
        const raw = inviteTokenFromUrl || fromStorage;
        const pendingInviteToken = raw?.trim() ? parseInviteTokenFromInput(raw.trim()) : '';
        console.log(LOG_PREFIX, 'After sign-in', {
          inviteTokenFromUrl: inviteTokenFromUrl ? `${inviteTokenFromUrl.slice(0, 12)}...` : null,
          fromStorage: fromStorage ? `${fromStorage.slice(0, 12)}...` : null,
          extractedToken: pendingInviteToken ? `${pendingInviteToken.slice(0, 8)}...` : null,
          hasPendingInvite: !!pendingInviteToken,
        });

        if (pendingInviteToken) {
          await AsyncStorage.removeItem(PENDING_INVITE_KEY);
          const completed = await getOnboardingCompleted(data.user.id);
          console.log(LOG_PREFIX, 'Pending invite path', { completed });

          if (completed) {
            let hasHousehold = false;
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('household_id')
                .eq('id', data.user.id)
                .maybeSingle() as { data: { household_id: string | null } | null };
              hasHousehold = !!profile?.household_id;
            } catch (e) {
              console.warn(LOG_PREFIX, 'Profile fetch failed', e);
            }
            const joinInviteUrl = `/join-invite?token=${encodeURIComponent(pendingInviteToken)}`;
            console.log(LOG_PREFIX, 'Redirect decision', { hasHousehold, willGoTo: hasHousehold ? '(tabs)' : '/join-invite' });
            if (hasHousehold) {
              router.replace('/(tabs)');
            } else {
              if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.location.href = joinInviteUrl;
              } else {
                router.replace(joinInviteUrl as any);
              }
            }
            setLoading(false);
            return;
          }
          await AsyncStorage.setItem(PENDING_INVITE_KEY, pendingInviteToken);
          console.log(LOG_PREFIX, 'Redirect to onboarding (invite token restored to storage)');
          router.replace('/(auth)/onboarding');
          setLoading(false);
          return;
        }
        const completed = await getOnboardingCompleted(data.user.id);
        console.log(LOG_PREFIX, 'No pending invite', { completed, willGoTo: completed ? '(tabs)' : 'onboarding' });
        if (completed) {
          router.replace('/(tabs)');
        } else {
          router.replace('/(auth)/onboarding');
        }
      }
    } catch (err: any) {
      setError('Something went wrong while signing in. Please try again.');
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = email && password;

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Image
                source={require('@/assets/images/icon.png')}
                style={styles.logo}
                resizeMode="contain"
                accessibilityLabel="Sage Chef logo"
              />
            </View>
            <Text style={styles.title}>Welcome back to Sage Chef</Text>
          </View>

          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Ionicons name="lock-closed" size={20} color={Brand.primary} />
              <Text style={styles.cardTitle}>Sign In</Text>
            </View>

            {error ? (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle" size={16} color={Brand.destructive} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}

            <Input
              testID="email-input"
              label="Email"
              placeholder="you@example.com"
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              value={email}
              onChangeText={setEmail}
              style={styles.input}
            />

            <Input
              testID="password-input"
              label="Password"
              placeholder="Enter your password"
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password"
              {...(Platform.OS === 'ios' &&
                (Constants.expoConfig?.extra as { isE2E?: boolean } | undefined)?.isE2E && {
                  textContentType: 'none' as const,
                })}
              value={password}
              onChangeText={setPassword}
              style={styles.input}
            />

            <View style={styles.forgotPasswordRow}>
              <Button
                variant="ghost"
                onPress={() => router.push('/(auth)/forgot-password')}
                style={styles.forgotPasswordButton}
              >
                Forgot password?
              </Button>
            </View>

            <Button
              testID="login-btn"
              onPress={handleSignIn}
              disabled={!isFormValid || loading}
              loading={loading}
              style={styles.submitButton}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <View style={styles.footer}>
              <Text style={styles.footerText}>Don&apos;t have an account?</Text>
              <Button
                variant="ghost"
                onPress={() => router.push('/(auth)/sign-up')}
                style={styles.linkButton}
              >
                Sign Up
              </Button>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    justifyContent: 'center',
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 32,
    alignItems: 'center',
  },
  logoContainer: {
    width: 100,
    height: 100,
    marginBottom: 20,
    borderRadius: 24,
    overflow: 'hidden',
  },
  logo: {
    width: '100%',
    height: '100%',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Brand.primary,
    textAlign: 'center',
  },
  card: {
    backgroundColor: Neutrals.whiteFaded70,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: Neutrals.whiteFaded70,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 2,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Brand.destructiveLight,
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: Brand.destructive,
  },
  input: {
    marginBottom: 16,
  },
  forgotPasswordRow: {
    alignItems: 'flex-end',
    marginTop: -8,
    marginBottom: 8,
  },
  forgotPasswordButton: {
    paddingHorizontal: 0,
  },
  submitButton: {
    marginTop: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 24,
    gap: 8,
  },
  footerText: {
    fontSize: 14,
    color: Neutrals.textSecondary,
  },
  linkButton: {
    paddingHorizontal: 0,
  },
});
